import { useEffect, useState } from "react";
import { Save, Timer } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { inputClass, selectClass } from "../components/FormField";
import { fetchDeadlines, saveDeadlines, type DeadlineSetting } from "../api/claimsApi";
import { fetchAdminInsurers, type AdminInsurer } from "../api/insurerApi";

/**
 * Administration SafeCheck : délai de chaque niveau du circuit d'une facture, par défaut ou propre à un assureur.
 * Chaque niveau démarre quand le précédent se termine ; les listes signalent les factures en retard.
 */
export function DeadlinesPage() {
  const { showToast } = useToast();
  const [insurers, setInsurers] = useState<AdminInsurer[]>([]);
  const [insurerId, setInsurerId] = useState<number | undefined>(undefined);
  const [rows, setRows] = useState<DeadlineSetting[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdminInsurers().then(setInsurers).catch(() => setInsurers([]));
  }, []);

  const apply = (settings: DeadlineSetting[], forInsurer: number | undefined) => {
    setRows(settings);
    setValues(
      Object.fromEntries(
        settings.map((row) => {
          const value = forInsurer ? row.days : row.default_days;
          return [row.stage, value === null || value === undefined ? "" : String(value)];
        }),
      ),
    );
  };

  useEffect(() => {
    setError(null);
    fetchDeadlines(insurerId)
      .then((settings) => apply(settings, insurerId))
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, [insurerId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const days = Object.fromEntries(
        Object.entries(values).map(([stage, raw]) => [stage, raw.trim() === "" ? null : Number(raw.replace(",", "."))]),
      );
      apply(await saveDeadlines(days, insurerId), insurerId);
      showToast("Délais de traitement enregistrés.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Breadcrumbs homeHref="/dashboard/admin" items={[{ label: "Délais de traitement" }]} />

      <section className="theme-card p-5">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Timer size={20} className="text-primary" />
          Délais de traitement
        </h1>
        <p className="mt-1 text-sm text-muted">
          Délai accordé à chaque niveau du circuit d'une facture, en jours. Le niveau démarre quand le précédent se
          termine ; les listes de factures, la file du Sinistre et le tableau de bord de l'assureur signalent les retards.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
          <label htmlFor="deadline-insurer" className="text-muted">Délais de :</label>
          <select
            id="deadline-insurer"
            value={insurerId ?? ""}
            onChange={(e) => setInsurerId(e.target.value ? Number(e.target.value) : undefined)}
            className={`${selectClass} w-auto`}
          >
            <option value="">Tous les assureurs (par défaut)</option>
            {insurers.map((insurer) => (
              <option key={insurer.id} value={insurer.id}>{insurer.name}</option>
            ))}
          </select>
        </div>
        {insurerId ? (
          <p className="mt-2 text-xs text-muted">Laissez une case vide pour appliquer le délai par défaut à cet assureur.</p>
        ) : null}
        {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
      </section>

      <section className="theme-card overflow-x-auto p-5">
        <table className="w-full min-w-[36rem] text-sm">
          <thead className="text-left text-xs uppercase text-muted">
            <tr>
              <th className="py-2">Niveau de traitement</th>
              <th className="py-2">Responsable</th>
              {insurerId ? <th className="py-2 text-right">Par défaut</th> : null}
              <th className="py-2 text-right">Délai (jours)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.stage} className="border-t border-border">
                <td className="py-2 text-foreground">
                  <span className="mr-2 text-xs text-muted">{index + 1}.</span>
                  {row.label}
                </td>
                <td className="py-2 text-muted">{row.actor}</td>
                {insurerId ? <td className="py-2 text-right tabular-nums text-muted">{row.default_days} j</td> : null}
                <td className="py-2 text-right">
                  <input
                    aria-label={`Délai de ${row.label}`}
                    inputMode="decimal"
                    value={values[row.stage] ?? ""}
                    placeholder={insurerId ? String(row.default_days) : ""}
                    onChange={(e) => setValues((current) => ({ ...current, [row.stage]: e.target.value }))}
                    className={`${inputClass} ml-auto h-9 w-24 text-right`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
          >
            <Save size={15} />
            Enregistrer
          </button>
        </div>
      </section>
    </div>
  );
}
